// GST helpers. Plan prices are BASE fees; 18% GST is added on top, so the
// amount charged at checkout is base + GST.

export const GST_RATE = 0.18;

export type GstBreakdown = {
  /** Fee excluding tax, rounded to the rupee. */
  base: number;
  /** 18% GST portion, rounded to the rupee. */
  gst: number;
  /** Total actually charged at checkout (base + GST). */
  total: number;
};

/** Add 18% GST on top of a base fee. */
export function addGst(baseInr: number): GstBreakdown {
  const base = Math.round(baseInr);
  const gst = Math.round(base * GST_RATE);
  return { base, gst, total: base + gst };
}

/** Split a tax-inclusive total back into base fee + 18% GST. */
export function splitGstInclusive(totalInr: number): GstBreakdown {
  const total = Math.round(totalInr);
  const base = Math.round(total / (1 + GST_RATE));
  return { base, gst: total - base, total };
}

/** Breakdown from a payment row; uses stored base/GST when available. */
export function breakdownFromPayment(p: {
  amount_paise: number;
  base_amount_paise?: number | null;
  gst_amount_paise?: number | null;
}): GstBreakdown {
  if (p.base_amount_paise != null && p.gst_amount_paise != null) {
    return {
      base: Math.round(p.base_amount_paise / 100),
      gst: Math.round(p.gst_amount_paise / 100),
      total: Math.round(p.amount_paise / 100),
    };
  }
  return splitGstInclusive(p.amount_paise / 100);
}

/** Same split from a paise amount (as stored on payments.amount_paise). */
export function splitGstFromPaise(amountPaise: number): GstBreakdown {
  return splitGstInclusive(amountPaise / 100);
}

export function formatInr(amount: number): string {
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

/** e.g. "₹3,000 + ₹540 (18% GST) = ₹3,540 Total" */
export function gstAddedLabel(baseInr: number): string {
  const { base, gst, total } = addGst(baseInr);
  return `${formatInr(base)} + ${formatInr(gst)} (18% GST) = ${formatInr(total)} Total`;
}

